import { useCallback, useEffect, useMemo, useState } from "react";
import { searchStudents, deactiveStudent, getStudentFilterValues } from "../../../api/studentsApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import GlobalFeedback from "../../../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";
import { GlobalModal } from "../../../../../global/GlobalModal";
import { GlobalButton } from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../../global/directory";
import {
  STUDENT_DIRECTORY_COLUMN_DEFS,
  STUDENT_DIRECTORY_COLUMN_VISIBILITY_KEY,
  STUDENT_DIRECTORY_PAGE_SIZE,
} from "../studentRegistrationListConstants";
import { loadStudentDirectoryVisibility, studentDirectoryCellValue } from "../studentRegistrationListHelpers";
import { filterActiveMasterRows } from "../../../../memberships/utils/masterLookupUtils";
import "../../../styles/StudentsListTable.css";
import "../../../../user-builder/styles/userBuilderTable.css";

export default function StudentRegistrationDirectorySection({ refreshKey = 0, sports = [], onEditStudent }) {
  const [students, setStudents] = useState([]);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const [totalStudents, setTotalStudents] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [studentToDeactivate, setStudentToDeactivate] = useState(null);
  const [deactivating, setDeactivating] = useState(false);

  const [draftFilters, setDraftFilters] = useState({
    search: "",
    filterColumn: "",
    filterValue: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    filterColumn: "",
    filterValue: "",
  });

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadStudentDirectoryVisibility(STUDENT_DIRECTORY_COLUMN_VISIBILITY_KEY);
    const initial = {};
    for (const c of STUDENT_DIRECTORY_COLUMN_DEFS) {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    }
    return initial;
  });

  const [fetchedFilterValueOptions, setFetchedFilterValueOptions] = useState([]);
  const [filterValuesLoading, setFilterValuesLoading] = useState(false);

  useEffect(() => {
    const col = draftFilters.filterColumn;
    if (!col || col === "sport_name") {
      setFetchedFilterValueOptions([]);
      setFilterValuesLoading(false);
      return;
    }
    let cancelled = false;
    setFilterValuesLoading(true);
    setFetchedFilterValueOptions([]);
    getStudentFilterValues(col)
      .then((vals) => {
        if (!cancelled) {
          setFetchedFilterValueOptions(vals.map((v) => ({ value: v, label: v })));
        }
      })
      .catch(() => {
        if (!cancelled) setFetchedFilterValueOptions([]);
      })
      .finally(() => {
        if (!cancelled) setFilterValuesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [draftFilters.filterColumn, refreshKey]);

  /** Options exclude empty "All" — GlobalSelectField already adds placeholderOption "All". */
  const studentFilterValueOptions = useMemo(() => {
    if (draftFilters.filterColumn === "sport_name") {
      return filterActiveMasterRows(sports).map((sp) => ({
        value: String(sp.id),
        label: sp.sport_name || `Sport ${sp.id}`,
      }));
    }
    return fetchedFilterValueOptions;
  }, [draftFilters.filterColumn, sports, fetchedFilterValueOptions]);

  useEffect(() => {
    const t = setTimeout(() => setAppliedFilters({ ...draftFilters }), 400);
    return () => clearTimeout(t);
  }, [draftFilters]);

  useEffect(() => {
    setPage(1);
  }, [appliedFilters.search, appliedFilters.filterColumn, appliedFilters.filterValue]);

  const visibleDataColumns = useMemo(
    () => STUDENT_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility],
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleDataColumns.map((c) => ({ key: c.id, label: c.label })),
      {
        key: "actions",
        label: "Actions",
        className: "ub-table__col-actions",
      },
    ],
    [visibleDataColumns],
  );

  const fetchStudents = useCallback(async () => {
    try {
      const s = appliedFilters.search.trim();
      const fc = appliedFilters.filterColumn;
      const fv = appliedFilters.filterValue;

      const payload = { page, limit: STUDENT_DIRECTORY_PAGE_SIZE };

      if (!fc) {
        if (s) payload.searchName = s;
      } else {
        switch (fc) {
          case "code":
            if (fv) payload.codeExact = fv;
            else if (s) payload.searchCode = s;
            break;
          case "full_name":
            if (fv) payload.nameExact = fv;
            else if (s) payload.searchName = s;
            break;
          case "email":
            if (fv) payload.emailExact = fv;
            else if (s) payload.email = s;
            break;
          case "contact_number":
            if (fv) payload.phoneExact = fv;
            else if (s) payload.phone = s;
            break;
          case "sport_name":
            if (fv) payload.sportId = fv;
            break;
          case "gender":
            if (fv) payload.genderExact = fv;
            else if (s) payload.gender = s;
            break;
          case "address":
            if (fv) payload.addressExact = fv;
            else if (s) payload.address = s;
            break;
          case "guardian_name":
            if (fv) payload.guardianNameExact = fv;
            else if (s) payload.guardianName = s;
            break;
          case "emergency_contact":
            if (fv) payload.emergencyContactExact = fv;
            else if (s) payload.emergencyContact = s;
            break;
          case "date_of_birth":
            if (fv) payload.dobExact = fv;
            else if (s) payload.dob = s;
            break;
          case "created_at":
            if (fv) payload.studentCreatedAtExact = fv;
            else if (s) payload.studentCreatedAt = s;
            break;
          case "mem_registration_date":
            if (fv) payload.studentMemRegDateExact = fv;
            else if (s) payload.studentMemRegDate = s;
            break;
          case "membership_name":
            if (fv) payload.membershipNameExact = fv;
            else if (s) payload.membershipName = s;
            break;
          default:
            if (s) payload.searchName = s;
        }
      }

      const result = await searchStudents(payload);

      if (result.success) {
        setStudents(result.students || []);
        setTotalPages(result.meta.totalPages);
        setTotalStudents(result.meta.total);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [page, appliedFilters.search, appliedFilters.filterColumn, appliedFilters.filterValue]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents, refreshKey]);

  const getExportData = useCallback(() => {
    const headers = visibleDataColumns.map((c) => c.label);
    const rows = students.map((s) =>
      visibleDataColumns.map((col) => String(studentDirectoryCellValue(col.id, s) ?? "")),
    );
    return { headers, rows };
  }, [students, visibleDataColumns]);

  const handleDeactivateClick = (student) => {
    setStudentToDeactivate(student);
    setShowConfirmModal(true);
  };

  const handleConfirmDeactivate = async () => {
    if (!studentToDeactivate) return;
    setDeactivating(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deactiveStudent(studentToDeactivate.id, csrfToken);
      setShowConfirmModal(false);
      setStudentToDeactivate(null);
      setSuccessMsg("Student removed successfully.");
      fetchStudents();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeactivating(false);
    }
  };

  const filters = (
    <GlobalDirectoryFiltersSection
      className="ub-user-directory-filters"
      searchLabel="Search by student name"
      searchPlaceholder="Type student name"
      searchValue={draftFilters.search}
      onSearchChange={(v) => setDraftFilters((f) => ({ ...f, search: v }))}
      columnLabel="Column"
      columnValue={draftFilters.filterColumn}
      onColumnChange={(v) =>
        setDraftFilters((f) => ({
          ...f,
          filterColumn: v,
          filterValue: "",
        }))
      }
      gridColumnDefs={STUDENT_DIRECTORY_COLUMN_DEFS}
      columnPlaceholderOption="Select"
      valueLabel="Value"
      valueValue={draftFilters.filterValue}
      onValueChange={(v) => setDraftFilters((f) => ({ ...f, filterValue: v }))}
      valueOptions={studentFilterValueOptions}
      valuePlaceholderOption="All"
      valueSelectDisabled={
        Boolean(draftFilters.filterColumn) &&
        draftFilters.filterColumn !== "sport_name" &&
        filterValuesLoading
      }
      onClear={() =>
        setDraftFilters({
          search: "",
          filterColumn: "",
          filterValue: "",
        })
      }
      clearFilterLabel="Clear filter"
      secondaryStart={
        <GlobalDirectoryColumnPicker
          columnDefs={STUDENT_DIRECTORY_COLUMN_DEFS}
          visibility={columnVisibility}
          onVisibilityChange={setColumnVisibility}
          storageKey={STUDENT_DIRECTORY_COLUMN_VISIBILITY_KEY}
          panelId="student-registration-column-panel"
        />
      }
      secondaryEnd={
        <GlobalDirectoryExportMenu
          getExportData={getExportData}
          filenameBase="student-registration-directory"
          buttonLabel="Export as"
        />
      }
    />
  );

  const grid = (
    <GlobalDirectoryGridSection
      loading={false}
      headers={tableHeaders}
      isEmpty={students.length === 0}
      emptyMessage="No students match your filters."
      tableAriaLabel="Students list"
      wrapClassName="ub-table-wrap"
      tableClassName="ub-table"
    >
      {students.map((student) => (
        <tr key={student.id}>
          {visibleDataColumns.map((col) => (
            <td key={col.id}>{studentDirectoryCellValue(col.id, student)}</td>
          ))}
          <td className="ub-table__col-actions">
            <div className="ub-table-actions">
              <GlobalTableActionButton
                action="edit"
                title="Edit student"
                onClick={() => onEditStudent?.(student.id)}
              />
              <GlobalTableActionButton
                action="delete"
                title="Remove student"
                onClick={() => handleDeactivateClick(student)}
              />
            </div>
          </td>
        </tr>
      ))}
    </GlobalDirectoryGridSection>
  );

  const pagination = (
    <GlobalDirectoryPaginationSection
      page={page}
      totalPages={totalPages ?? 1}
      total={totalStudents ?? 0}
      pageSize={STUDENT_DIRECTORY_PAGE_SIZE}
      onPageChange={setPage}
      label="Students pagination"
    />
  );

  return (
    <div id="sr-list" className="student-reg-panel" style={{ scrollMarginTop: "1rem" }}>
      <h3>Students List</h3>
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalDirectoryShell
        filters={filters}
        grid={grid}
        pagination={pagination}
        className="sr-directory-shell gds-shell--compact"
      />

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !deactivating && setShowConfirmModal(false)}
        title="Remove student"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton onClick={() => setShowConfirmModal(false)} disabled={deactivating}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDeactivate} disabled={deactivating}>
              {deactivating ? "Removing…" : "Remove"}
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove student <b>{studentToDeactivate?.full_name}</b> ({studentToDeactivate?.code})? They
          will be marked inactive and hidden from this list.
        </p>
      </GlobalModal>

      <GlobalFeedback type="error" text={error} />
    </div>
  );
}
